import { useScreenCue } from "./ScreenCue";
import { useExit } from "./useExit";
import { useSheetLayer } from "../devsheet-hooks";
import { useEffect, useRef, useState } from "preact/hooks";
import { APK_URL, TESTFLIGHT_URL } from "../../shared/stores.ts";

const HIDE_KEY = "op.hideInstallHint";
/** "Not now" puts the guide away for a week on this device: an epoch ms it stays hidden until. */
const SNOOZE_KEY = "op.installHintSnoozedUntil";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
/** TestFlight itself on the App Store, for phones that do not have it yet. */
const TESTFLIGHT_APP_URL = "https://apps.apple.com/app/testflight/id899247664";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type CapacitorGlobal = { isNativePlatform?: () => boolean; getPlatform?: () => string };

/**
 * Which native shell the site is running in (docs/ios.md, docs/android.md), or null in a browser.
 * The shell is recognised by Capacitor's bridge or, before the bridge is injected, by the
 * "OmiesPeonsApp" user agent marker both shells append; the platform comes from the bridge, else
 * from the user agent. The Android app takes photos through the Camera plugin
 * (src/native-camera.ts) and leaves notifications to Chrome; the iOS app uses APNs.
 */
export function nativePlatform(): "ios" | "android" | null {
  const cap = (window as Window & { Capacitor?: CapacitorGlobal }).Capacitor;
  if (!(cap?.isNativePlatform?.() === true || navigator.userAgent.includes("OmiesPeonsApp"))) return null;
  const platform = cap?.getPlatform?.();
  if (platform === "android" || platform === "ios") return platform;
  return /Android/i.test(navigator.userAgent) ? "android" : "ios";
}

/** True inside either native shell: Capacitor's bridge is present, or its user agent marker. */
export function isNativeShell(): boolean {
  return nativePlatform() !== null;
}

/** True when launched from a home-screen icon or the native shell rather than a browser tab. */
export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    isNativeShell() ||
    nav.standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches
  );
}

function isMobile(): boolean {
  const ua = navigator.userAgent;
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod|Android/i.test(ua) || iPadOS;
}

function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function hidden(): boolean {
  try {
    if (localStorage.getItem(HIDE_KEY) === "1") return true;
    return Date.now() < Number(localStorage.getItem(SNOOZE_KEY) ?? 0);
  } catch {
    return false;
  }
}

/** Should the guide be shown on this device right now? Exposed for the app shell. */
export function shouldShowInstallHint(): boolean {
  return isMobile() && !isStandalone() && !hidden();
}

/**
 * Bottom sheet that walks a phone user through "Add to Home Screen". Shown
 * only in a mobile browser tab, never when already installed. "Don't ask
 * again" is remembered per device.
 */
export function InstallHint({ onClose }: { onClose: () => void }) {
  // Escape, the backdrop and the close buttons leave through the stepped exit (src/components/useExit.ts).
  const exit = useExit(onClose);
  useScreenCue(exit.closing);
  const [dontAsk, setDontAsk] = useState(false);
  const dontAskRef = useRef(false); // read synchronously in close(), independent of render timing
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const ios = isIOS();

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  // Put away without "Don't ask again" (Not now, the backdrop, Escape): back in a week.
  function close() {
    try {
      if (dontAskRef.current) localStorage.setItem(HIDE_KEY, "1");
      else localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    } catch {
      /* ignore */
    }
    exit.close();
  }
  const sheetRef = useSheetLayer(!exit.closing, close);

  async function install() {
    if (!installEvent) return;
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") exit.close();
  }

  return (
    <div class={`sheet-backdrop${exit.cls}`} ref={exit.ref} onClick={close}>
      <div class="sheet" ref={sheetRef} role="dialog" aria-modal="true" tabIndex={-1} aria-label="Add to Home Screen" onClick={(e) => e.stopPropagation()}>
        <img class="sheet-icon" src="/ui/peon-original@3x.png" alt="" />
        <h2>Put the peons on your Home Screen</h2>
        <p class="muted">Full screen, faster, and you stay signed in.</p>
        {ios ? (
          <>
            {/* The iOS app (docs/ios.md) comes through TestFlight: the link installs TestFlight's page first if needed. */}
            <a class="btn btn-primary btn-block" href={TESTFLIGHT_URL} target="_blank" rel="noopener">
              Get the app
            </a>
            <p class="muted small install-note">
              It installs through TestFlight, Apple's app for apps in testing.{" "}
              <a href={TESTFLIGHT_APP_URL} target="_blank" rel="noopener">
                Get TestFlight first
              </a>{" "}
              if you don't have it.
            </p>
            <p class="muted small install-or">Or add this page to your Home Screen:</p>
          </>
        ) : (
          <>
            {/* The Android app (docs/android.md): the same peons in a real app icon, downloaded from here. */}
            <a class="btn btn-primary btn-block" href={APK_URL} download="omies-peons.apk">
              Get the app
            </a>
            <p class="muted small install-note">Android asks once to allow installs from your browser. Needs Chrome.</p>
            <p class="muted small install-or">Or add this page to your Home Screen:</p>
          </>
        )}
        {installEvent ? (
          <button class="btn btn-ghost btn-block" onClick={install}>
            Install
          </button>
        ) : ios ? (
          <ol class="steps">
            <li>
              Tap <strong>Share</strong> <span class="kbd">⎋</span> in Safari.
            </li>
            <li>
              Tap <strong>Add to Home Screen</strong>.
            </li>
            <li>
              Tap <strong>Add</strong>.
            </li>
          </ol>
        ) : (
          <ol class="steps">
            <li>
              Open the browser menu <span class="kbd">⋮</span>.
            </li>
            <li>
              Tap <strong>Add to Home screen</strong> or <strong>Install app</strong>.
            </li>
            <li>
              Confirm with <strong>Add</strong> or <strong>Install</strong>.
            </li>
          </ol>
        )}
        <label class="field toggle">
          <input type="checkbox" checked={dontAsk} onChange={(e) => {
              dontAskRef.current = e.currentTarget.checked;
              setDontAsk(e.currentTarget.checked);
            }} />
          <span>Don't ask again on this device</span>
        </label>
        <button class="btn btn-ghost btn-block" onClick={close}>
          Not now
        </button>
      </div>
    </div>
  );
}
